/**
 * useMerchantMembers Hook - Aggregator Pattern
 *
 * Fetches and returns merchant member data.
 */

import { useListMembers } from '@/generated/hooks';
import { getMillisecondsSince } from '@zlinkx/shared-v2';
import type { ListMembersParamsType, ListMembersQueryType } from '@zlinkx/shared-v2';
import type { AggregatorPattern } from '../../types/hook-patterns.types';
import type {
	MerchantMembersData,
	MerchantMembersComputed,
	UseMerchantMembersOptionsInternal
} from '@/types/controller.types';
import { transformMerchantMembers } from '@/utils/api-transformers';

const DEFAULT_MEMBER_QUERY_LIMIT = 50;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toMemberRole(role: string | undefined): ListMembersQueryType['role'] | undefined {
	if (role === 'admin' || role === 'staff' || role === 'member') {
		return role;
	}

	return undefined;
}

function buildMembersQuery(
	options: UseMerchantMembersOptionsInternal & {
		queryParams?: Partial<ListMembersQueryType>;
	},
): ListMembersQueryType {
	const roleFromFilters = toMemberRole(options.filters?.role);
	const rawSearch = options.filters?.search;
	const trimmedSearch = typeof rawSearch === 'string' ? rawSearch.trim() : '';
	const searchFromFilters = trimmedSearch.length > 0 ? trimmedSearch : undefined;

	return {
		limit: options.queryParams?.limit ?? DEFAULT_MEMBER_QUERY_LIMIT,
		cursor: options.queryParams?.cursor,
		role: options.queryParams?.role ?? roleFromFilters,
		search: options.queryParams?.search ?? searchFromFilters,
	};
}

function getAverageTenureDays(members: MerchantMembersData['members']): number {
	const tenureDays = members
		.map(member => member.memberSince)
		.filter((memberSince): memberSince is Date => memberSince instanceof Date)
		.map(memberSince => getMillisecondsSince(memberSince))
		.filter(elapsedMs => Number.isFinite(elapsedMs))
		.map(elapsedMs => Math.max(0, elapsedMs / MS_PER_DAY));

	if (tenureDays.length === 0) {
		return 0;
	}

	return Math.round(tenureDays.reduce((sum, value) => sum + value, 0) / tenureDays.length);
}

/**
 * Aggregates merchant member data
 */
export function useMerchantMembers(
	options: UseMerchantMembersOptionsInternal & {
		merchantId: ListMembersParamsType['merchantId'];
		currentUserId?: string | null;
		queryParams?: Partial<ListMembersQueryType>;
	},
): AggregatorPattern<MerchantMembersData, MerchantMembersComputed> & {
	hasNextPage: boolean;
	fetchNextPage: () => void;
	isFetchingNextPage: boolean;
} {
	const {
		merchantId,
		currentUserId = null,
		enabled = true,
	} = options;
	const query = buildMembersQuery(options);

	// Fetch member data
	const membersQuery = useListMembers(
		{ merchantId, query },
		{ enabled: Boolean(merchantId) && enabled }
	);

	const members = transformMerchantMembers(membersQuery.data);

	// Categorize members using transformed data
	const admins = members.filter(m => m.role === 'admin');
	const staffMembers = members.filter(m => m.role === 'staff');
	const activeMembers = members.filter(m => m.status === 'active');
	const disabledMembers = members.filter(m => m.status !== 'active');
	const hasMultipleAdmins = admins.length > 1;
	const owner = admins[0] ?? null;
	const averageTenureDays = getAverageTenureDays(members);
	const needsMoreAdmins =
		members.length > 0 && admins.length < Math.max(1, Math.ceil(members.length / 5));

	// Basic data
	const data: MerchantMembersData = {
		members,
		adminMembers: admins,
		staffMembers,
		owner,
		activeMembers,
		disabledMembers,
	};

	// Simple computed values
	const computed: MerchantMembersComputed = {
		totalCount: members.length,
		adminCount: admins.length,
		staffCount: staffMembers.length,
		activeCount: activeMembers.length,
		disabledCount: disabledMembers.length,
		activityRate: members.length > 0 ? (activeMembers.length / members.length) * 100 : 0,
		hasMultipleAdmins,
		hasStaff: staffMembers.length > 0,
		hasOwner: owner !== null,
		averageTenureDays,
		teamSize: members.length === 1 ? 'solo' : members.length <= 5 ? 'small' : members.length <= 20 ? 'medium' : 'large',
		isLastAdmin: currentUserId !== null ? admins.length === 1 && admins[0]?.userId === currentUserId : false,
		needsMoreAdmins,
	};

	// Return aggregator pattern
	return {
		data,
		computed,
		isLoading: membersQuery.isLoading,
		error: membersQuery.error?.message ?? null,

		// Pagination (from paginated query hook)
		hasNextPage: membersQuery.hasNextPage,
		fetchNextPage: membersQuery.fetchNextPage,
		isFetchingNextPage: membersQuery.isFetchingNextPage,
	};
}

export default useMerchantMembers;
